import { useState } from 'react'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

import { maskValue } from 'utils/helpers'
import Button from 'components/Button'
import Spacer from 'components/Spacer'
import Column from 'components/Column'
import H3 from 'components/H3'

import './CardSectionStyles.css'

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
}

export default function SubscriptionForm({ info, onSuccess }) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async () => {
    setErrorMsg('')

    if (!stripe || !elements) {
      return
    }

    setLoading(true)

    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
      billing_details: {
        name: `${info.firstName} ${info.lastName}`,
        email: info.email,
        phone: info.phoneNumber,
      },
    })

    if (result.error) {
      setLoading(false)
      return setErrorMsg(result.error.message)
    }

    const items = [
      { price: process.env.REACT_APP_BASE_DB_MANAGING, quantity: 1 },
    ]

    if (info.databases > 1) {
      items.push({
        price: process.env.REACT_APP_ADDITIONAL_DB,
        quantity: info.databases - 1,
      })
    }

    if (info.hours > 8) {
      items.push({
        price: process.env.REACT_APP_ADDITIONAL_HOURS,
        quantity: info.hours - 8,
      })
    }

    try {
      const res = await axios.post(`${process.env.REACT_APP_API}/v1/sub`, {
        paymentMethod: result.paymentMethod.id,
        email: info.email,
        items,
      })

      const { id, client_secret, status } = res.data

      if (status === 'requires_action') {
        const confirm = await stripe.confirmCardPayment(client_secret)

        if (confirm.error) {
          setLoading(false)
          return setErrorMsg('There was an issue', confirm.error)
        }

        return onSuccess(id)
      }

      return onSuccess(id)
    } catch (e) {
      setLoading(false)
      return setErrorMsg(e.response.data.message)
    }
  }

  return (
    <div className="content">
      <p className="description">
        {t('home.database.modal.footer', {
          databases: info.databases,
          hours: info.hours,
        })}
      </p>
      <Spacer height={10} />
      <p className="description_price">
        {t('home.database.modal.footer_price', {
          total: maskValue(info.totalValue),
        })}
      </p>

      <Spacer height={30} />

      <Column align="center">
        <div className="card-element">
          <H3>{t('checkout.payment_info')}</H3>

          <Spacer height={20} />

          {errorMsg && <div className="error-msg">{errorMsg}</div>}

          <Spacer height={20} />

          <Column align="center">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
            <Spacer height={30} />
            <Button
              type="button"
              className="primary"
              label={t('button.submit')}
              onClick={handleSubmit}
              loading={loading}
            />
          </Column>
        </div>
      </Column>
    </div>
  )
}
